import { queryView, type QueryResultLike, type QueryView } from '@krp/client-runtime'
import { firstOfMonth, isoDayOf, lastOfMonth, pluralRu, type ChoiceChip, type DayRange } from '@krp/ui-kit'

import { formatInstant, formatKopecks } from '../../../shared/uc1b/format'
import { periodEnd, periodStart, type MyShiftsArgs, type ShiftsArgs } from '../../receipts/api/receiptsApi'
import { NO_SHOP, ROOT_NODE, nodePatch, selectionText, selectedNodeOf, treeNodesOf } from '../../receipts/model/tree'
import type { MyShift, ReceiptRegister, ReceiptRegisters, ReceiptShift, ReceiptShifts } from '../../receipts/model/types'
import type { RegisterShiftsInput, ShiftRowVm, ShiftState, ShiftsView, ShiftsVm, ShiftsVmInput } from './types'

/**
 * Модель раздела «Смены» кабинета AQSI — чистые функции: какие кассы читать, какие аргументы у запросов, как из смен касс,
 * «моих смен» и отбора собрать строки экрана. Кабинет в списке смен отдаёт только номер и время обновления; открытие и
 * закрытие — карточка смены. Карточку служба читает для последней смены каждой кассы (открытой может быть только она: касса
 * не открывает новую смену, не закрыв прежнюю), время остальных — по журналу работы («мои смены») или по чекам журнала.
 */

/** Период по умолчанию — текущий месяц по сегодня. */
export function shiftsRangeOf(view: Pick<ShiftsView, 'from' | 'to'>, today: string): DayRange {
  if (view.from === '' || view.to === '') return { from: firstOfMonth(today), to: today }
  return { from: view.from, to: view.to }
}

/** Весь месяц по сегодня (стрелки и «Весь месяц»): будущие дни не выбираются. */
export function monthRangeOf(month: string, today: string): DayRange {
  const last = lastOfMonth(month)
  return { from: firstOfMonth(month), to: last > today ? today : last }
}

/** Отбор места словами дерева чеков: узел → магазин или касса. */
function placeFilter(place: string) {
  const patch = nodePatch(place === '' ? ROOT_NODE : place) ?? {}
  return { shop: patch.shop ?? '', registerId: patch.registerId ?? '', shiftNumber: '' }
}

/** Кассы выбранного места: весь кабинет, магазин или одна касса. */
export function registersInPlace(registers: ReceiptRegisters | undefined, place: string): readonly ReceiptRegister[] {
  const list = registers?.registers ?? []
  const filter = placeFilter(place)
  if (filter.registerId !== '') return list.filter((register) => register.id === filter.registerId)
  if (filter.shop !== '') return list.filter((register) => (register.shop === '' ? NO_SHOP : register.shop) === filter.shop)
  return list
}

/** Аргументы «моих смен» за период: моменты с поясом браузера, как у прежней вкладки «Мои смены». */
export function myShiftsArgsOf(resourceId: string, range: DayRange): MyShiftsArgs | null {
  const from = periodStart(range.from)
  const to = periodEnd(range.to)
  return resourceId === '' || from === undefined || to === undefined ? null : { resourceId, from, to }
}

/** Список смен кассы без карточки. */
export function registerShiftsArgsOf(resourceId: string, registerId: string): ShiftsArgs {
  return { resourceId, registerId }
}

/** Тот же список с карточкой последней смены — когда номер последней известен; иначе `null`. */
export function latestShiftArgsOf(resourceId: string, registerId: string, list: ReceiptShifts | undefined): ShiftsArgs | null {
  if (list === undefined || list.shifts.length === 0) return null
  const latest = Math.max(...list.shifts.map((shift) => shift.number))
  return { resourceId, registerId, detailsFor: String(latest) }
}

const registerTitle = (register: ReceiptRegister | undefined, registerId: string): string => {
  if (register === undefined) return `Касса ${registerId}`
  const name = register.title === '' ? `Касса ${register.serialNumber}` : register.title
  return register.shop === '' || register.shop === NO_SHOP ? name : `${register.shop} · ${name}`
}

const dayOf = (instant: string): string => isoDayOf(new Date(instant))

const countText = (count: number) => `${count} ${pluralRu(count, 'чек', 'чека', 'чеков')}`

const STATE_TEXT: Readonly<Record<ShiftState, string>> = {
  open: 'Открыта',
  closed: 'Закрыта',
  unknown: 'Состояние не получено',
}

interface Candidate {
  row: ShiftRowVm
  start: string
  end: string
  cashiers: readonly string[]
}

function cabinetCandidate(register: ReceiptRegister | undefined, registerId: string, shift: ReceiptShift, latest: number, mine: MyShift | undefined, today: string): Candidate | null {
  const journal = shift.journal ?? null
  const state: ShiftState =
    shift.isOpen === true ? 'open'
      : shift.isOpen === false || shift.number < latest ? 'closed'
        : mine !== undefined ? (mine.endedAt === null ? 'open' : 'closed')
          : 'unknown'
  const start = shift.openedAt ?? mine?.startedAt ?? journal?.firstReceiptAt ?? shift.updatedAt
  if (start === null) return null
  const startDay = dayOf(start)
  const endInstant = shift.closedAt ?? mine?.endedAt ?? journal?.lastReceiptAt ?? shift.updatedAt
  const end = state === 'open' ? today : endInstant === null ? startDay : dayOf(endInstant)
  const openedBy = shift.openedByCashier ?? null
  const closedBy = shift.closedByCashier ?? null
  const cashiers = [...new Set([...(journal?.cashiers ?? []), ...(openedBy !== null ? [openedBy] : []), ...(closedBy !== null ? [closedBy] : [])])]
  const openedText = shift.openedAt !== null
    ? `Открыта ${formatInstant(shift.openedAt)}${openedBy !== null ? ` · ${openedBy}` : ''}`
    : mine !== undefined ? `Начало по журналу работы: ${formatInstant(mine.startedAt)}`
      : journal?.firstReceiptAt != null ? `Первый чек: ${formatInstant(journal.firstReceiptAt)}`
        : `Обновлена в кабинете: ${formatInstant(shift.updatedAt)}`
  const closedText = state === 'open' ? 'Смена ещё открыта'
    : shift.closedAt !== null ? `Закрыта ${formatInstant(shift.closedAt)}${closedBy !== null ? ` · ${closedBy}` : ''}`
      : mine?.endedAt != null ? `Конец по журналу работы: ${formatInstant(mine.endedAt)}`
        : journal?.lastReceiptAt != null ? `Последний чек: ${formatInstant(journal.lastReceiptAt)}`
          : null
  const cleared = shift.cleanup?.state === 'cleared' ? shift.cleanup : null
  const totalsText = journal !== null
    ? `${countText(journal.count)} · Приход ${formatKopecks(journal.saleKopecks)} · Возврат ${formatKopecks(journal.refundKopecks)}`
    : cleared !== null
      ? `${countText(cleared.count)} · Приход ${formatKopecks(cleared.saleKopecks)} · Возврат ${formatKopecks(cleared.refundKopecks)} (чеки в кабинете AQSI)`
      : 'Чеков этой смены в журнале нет'
  return {
    start: startDay,
    end: end < startDay ? startDay : end,
    cashiers,
    row: {
      key: `${registerId}:${shift.number}`,
      registerId,
      number: shift.number,
      numberText: `Смена № ${shift.number}`,
      registerText: registerTitle(register, registerId),
      state,
      stateText: STATE_TEXT[state],
      openedText,
      closedText,
      cashiersText: cashiers.length === 0 ? 'Кассир не указан' : cashiers.join(', '),
      totalsText,
      mine: mine !== undefined,
    },
  }
}

/** Моя смена, которой нет в прочитанном списке кабинета (кабинет не ответил или список длиннее предела): видна по журналу работы. */
function mineCandidate(register: ReceiptRegister | undefined, shift: MyShift, today: string): Candidate {
  const registerId = String(shift.deviceId)
  const state: ShiftState = shift.endedAt === null ? 'open' : 'closed'
  const start = dayOf(shift.startedAt)
  const end = shift.endedAt === null ? today : dayOf(shift.endedAt)
  return {
    start,
    end: end < start ? start : end,
    cashiers: [],
    row: {
      key: `${registerId}:${shift.shiftNumber}`,
      registerId,
      number: shift.shiftNumber,
      numberText: `Смена № ${shift.shiftNumber}`,
      registerText: register !== undefined ? registerTitle(register, registerId) : shift.deviceSerialNumber === '' ? `Касса ${registerId}` : `Касса ${shift.deviceSerialNumber}`,
      state,
      stateText: STATE_TEXT[state],
      openedText: `Начало по журналу работы: ${formatInstant(shift.startedAt)}`,
      closedText: shift.endedAt === null ? 'Смена ещё открыта' : `Конец по журналу работы: ${formatInstant(shift.endedAt)}`,
      cashiersText: 'Кассир не указан',
      totalsText: 'Итоги кабинета по смене не прочитаны',
      mine: true,
    },
  }
}

function resultOf(input: RegisterShiftsInput): QueryResultLike<ReceiptShifts> {
  // Список с карточкой последней смены — тот же список, только полнее; пока его нет, показываем список без карточки.
  return input.latest?.isSuccess === true && input.latest.data !== undefined ? input.latest : input.list
}

/** Состояние чтения смен всех касс места: ошибка одной кассы — ошибка (со словами службы), пока хоть одна читается — загрузка. */
function combinedQuery(results: readonly QueryResultLike<ReceiptShifts>[], registers: QueryView, empty: boolean): QueryView {
  if (registers.status !== 'ready' && registers.status !== 'empty') return registers
  const views = results.map((result) => queryView(result, () => false))
  const failed = views.find((view) => view.error !== null)
  if (failed !== undefined) return failed
  if (views.some((view) => view.status === 'loading' || view.status === 'idle')) return { status: 'loading', refreshing: false, error: null }
  return { status: empty ? 'empty' : 'ready', refreshing: views.some((view) => view.refreshing), error: null }
}

export function shiftsVmOf(input: ShiftsVmInput): ShiftsVm {
  const { resourceId, view, today } = input
  const range = shiftsRangeOf(view, today)
  const registersData = input.registers.isSuccess ? input.registers.data : undefined
  const registersQuery = queryView(input.registers, (data) => data.registers.length === 0)
  const place = placeFilter(view.place)
  const inPlace = registersInPlace(registersData, view.place)
  const byId = new Map(inPlace.map((register) => [register.id, register]))
  const mine = input.myShifts.isSuccess ? (input.myShifts.data ?? []) : []
  const mineOf = (registerId: string, number: number) => mine.find((shift) => String(shift.deviceId) === registerId && shift.shiftNumber === number)

  const candidates: Candidate[] = []
  const seen = new Set<string>()
  const warnings: string[] = []
  let timeless = 0
  const reads = input.shifts.filter((entry) => byId.has(entry.registerId))
  for (const entry of reads) {
    const result = resultOf(entry)
    const data = result.isSuccess ? result.data : undefined
    if (data === undefined) continue
    const register = byId.get(entry.registerId)
    if (data.state.warning !== null) warnings.push(`${registerTitle(register, entry.registerId)}: ${data.state.warning}`)
    const latest = data.shifts.reduce((max, shift) => Math.max(max, shift.number), 0)
    for (const shift of data.shifts) {
      const candidate = cabinetCandidate(register, entry.registerId, shift, latest, mineOf(entry.registerId, shift.number), today)
      seen.add(`${entry.registerId}:${shift.number}`)
      if (candidate === null) timeless += 1
      else candidates.push(candidate)
    }
  }
  for (const shift of mine) {
    const registerId = String(shift.deviceId)
    if (!byId.has(registerId) || seen.has(`${registerId}:${shift.shiftNumber}`)) continue
    candidates.push(mineCandidate(byId.get(registerId), shift, today))
  }
  if (timeless > 0) warnings.push(`${timeless} ${pluralRu(timeless, 'смена', 'смены', 'смен')} без известного времени не показаны: кабинет не сообщил, когда они были`)

  const inPeriod = candidates.filter((candidate) => candidate.start <= range.to && candidate.end >= range.from)
  const byMine = view.mineOnly ? inPeriod.filter((candidate) => candidate.row.mine) : inPeriod
  const byState = view.state === '' ? byMine : byMine.filter((candidate) => candidate.row.state === view.state)
  const names = new Map<string, number>()
  for (const candidate of byState) for (const name of candidate.cashiers) names.set(name, (names.get(name) ?? 0) + 1)
  const cashiers: ChoiceChip[] = [
    { id: '', label: 'Все кассиры' },
    ...[...names.entries()].sort(([left], [right]) => left.localeCompare(right, 'ru')).map(([name, count]) => ({ id: name, label: name, count })),
  ]
  // Выбранный кассир остаётся чипом, даже когда в периоде его смен нет: отбор виден и снимается.
  if (view.cashier !== '' && !names.has(view.cashier)) cashiers.push({ id: view.cashier, label: view.cashier, count: 0 })
  const shown = view.cashier === '' ? byState : byState.filter((candidate) => candidate.cashiers.includes(view.cashier))
  const rows = [...shown]
    .sort((left, right) => (right.start === left.start ? right.row.number - left.row.number : right.start < left.start ? -1 : 1))
    .map((candidate) => candidate.row)

  const stateCount = (state: ShiftState) => byMine.filter((candidate) => candidate.row.state === state).length
  const query = view.mineOnly && !input.myShifts.isSuccess && input.myShifts.isError
    ? queryView(input.myShifts, () => false)
    : combinedQuery(reads.map(resultOf), registersQuery, rows.length === 0)
  const month = firstOfMonth(range.from)

  return {
    resourceId,
    title: input.registers.data?.state.title ?? 'Кабинет AQSI',
    today,
    range,
    wholeMonth: range.from === month && range.to === monthRangeOf(month, today).to,
    nav: {
      query: registersQuery,
      nodes: treeNodesOf(registersData, undefined, place, view.collapsed),
      selectedId: selectedNodeOf(place),
      summaryText: selectionText(registersData, place),
    },
    query,
    rows,
    countText: `Смен: ${rows.length}`,
    states: [
      { id: '', label: 'Все', count: byMine.length },
      { id: 'open', label: 'Открытые', count: stateCount('open') },
      { id: 'closed', label: 'Закрытые', count: stateCount('closed') },
    ],
    state: view.state,
    cashiers,
    cashier: view.cashier,
    mineOnly: view.mineOnly,
    mineQuery: queryView(input.myShifts, () => false),
    warnings,
  }
}
